import { CADINU_MARK } from './cadinuMark'
import { SEAL_COLORS, type SealTone } from './sealSvg'

/**
 * The CADINU Lock padlock (Bez, 2026-10-09, round 2: "the vault looks like a ship's steering wheel"; decision: a heavy closed padlock).
 * One object for every lock surface: the landing hero, the create header and review, the certificate, the list rows, the share image
 * and the embeddable badge. A thick brushed-graphite body with bevels, rivets and a specular highlight; a closed steel shackle; the gold
 * CADINU Lock medal (the official mark) set into the body with the time served as a ring in the lock's status colour; a light along the
 * seam in that colour; a contact shadow. A lock that no longer protects anything is drawn OPEN, the shackle lifted out of the body: a released
 * lock (the metal tarnished, the light dimmed) and, since 2026-10-09 (r3), a lock past its date ("Ready to unlock": the owner can withdraw it
 * at any moment), which keeps its gold and its amber ring. The lock state badge's PARTIAL look has its own tone, `partial` (closed, amber), so
 * what `due` draws can never change it.
 *
 * Pure: a string of SVG built from numbers and fixed colours, no text, no <style>, ids prefixed by `uid`, so the page (React), the share
 * image (satori/resvg) and the badge draw the same object. The page animates the classes `shackle`, `seam` and `arc` (components/Seal.tsx).
 */
export const PADLOCK_VIEWBOX = '0 0 200 220'
export const PADLOCK_RATIO = 220 / 200

/** a lock's phase tone (SealTone), or `partial`: the lock state badge's partly locked look, never a lock's phase */
export type PadlockTone = SealTone | 'partial'

/** the status colour of the seam and the ring */
export const PADLOCK_LIT: Record<PadlockTone, string> = {
  locked: SEAL_COLORS.locked.lit,
  soon: SEAL_COLORS.soon.lit,
  due: SEAL_COLORS.due.lit,
  released: SEAL_COLORS.released.lit,
  vesting: SEAL_COLORS.vesting.lit,
  partial: '#f5b453',
}
/** the shackle: closed while the lock protects something, open once the owner can take it (due) or has taken it (released) */
export const PADLOCK_OPEN: Record<PadlockTone, boolean> = {
  locked: false,
  soon: false,
  vesting: false,
  partial: false,
  due: true,
  released: true,
}

export type PadlockOptions = {
  /** time served, or the released share of a vesting lock: 0..1 */
  progress: number
  tone: PadlockTone
  /** prefix of the gradient ids: two padlocks on one page must not share ids */
  uid: string
  /** small copies (list rows, badges, 64 px and under): no brushing, no rivets, a bolder ring */
  mini?: boolean
}

const n2 = (n: number) => (Math.round(n * 100) / 100).toString()
const MX = 100
const MY = 158

const polar = (r: number, deg: number) => {
  const a = ((deg - 90) * Math.PI) / 180
  return [MX + r * Math.cos(a), MY + r * Math.sin(a)] as const
}

const GOLD = { hi: '#fff3c4', mid: '#f2c75c', lo: '#a8741e', edge: '#6b4a12' }
const PEWTER = { hi: '#e3e8f5', mid: '#a9b4dc', lo: '#5d6690', edge: '#39405e' }

function mark(uid: string, cx: number, cy: number, w: number): string {
  const s = w / CADINU_MARK.width
  const h = CADINU_MARK.height * s
  return `<g transform="translate(${n2(cx - w / 2)} ${n2(cy - h / 2)}) scale(${(
    Math.round(s * 100000) / 100000
  ).toString()})"><g transform="${CADINU_MARK.transform}" fill="url(#${uid}-mark)"><path d="${CADINU_MARK.d}"/></g></g>`
}

export function padlockSvg({ progress, tone, uid, mini = false }: PadlockOptions): string {
  const p = Math.min(1, Math.max(0, Number.isFinite(progress) ? progress : 0))
  const open = PADLOCK_OPEN[tone]
  /** finished: the metal tarnished to pewter and the light dimmed (a released lock only; a due lock still holds its tokens) */
  const spent = tone === 'released'
  const lit = PADLOCK_LIT[tone]
  const m = spent ? PEWTER : GOLD
  const id = (n: string) => `${uid}-${n}`
  const url = (n: string) => `url(#${id(n)})`
  const parts: string[] = []

  parts.push(
    '<defs>',
    `<radialGradient id="${id(
      'halo',
    )}" cx="50%" cy="62%" r="55%"><stop offset="0.35" stop-color="${lit}" stop-opacity="${
      spent ? 0.08 : 0.26
    }"/><stop offset="1" stop-color="${lit}" stop-opacity="0"/></radialGradient>`,
    `<radialGradient id="${id(
      'ground',
    )}" cx="50%" cy="50%" r="50%"><stop offset="0" stop-color="#000" stop-opacity="0.75"/><stop offset="1" stop-color="#000" stop-opacity="0"/></radialGradient>`,
    // the shackle: a polished steel bar, lit from the left
    `<linearGradient id="${id(
      'bar',
    )}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#3b4677"/><stop offset="0.28" stop-color="#eef2ff"/><stop offset="0.55" stop-color="#9eaad8"/><stop offset="1" stop-color="#2a3263"/></linearGradient>`,
    // the body: graphite, lighter at the top edge, deep at the base
    `<linearGradient id="${id('body')}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${
      spent ? '#41465f' : '#5a6290'
    }"/><stop offset="0.24" stop-color="${
      spent ? '#272a40' : '#2c3157'
    }"/><stop offset="1" stop-color="#0c0e22"/></linearGradient>`,
    `<linearGradient id="${id(
      'side',
    )}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#ffffff" stop-opacity="0.16"/><stop offset="0.12" stop-color="#ffffff" stop-opacity="0"/><stop offset="0.88" stop-color="#000000" stop-opacity="0"/><stop offset="1" stop-color="#000000" stop-opacity="0.35"/></linearGradient>`,
    `<linearGradient id="${id(
      'bevel',
    )}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#dfe9ff" stop-opacity="0.65"/><stop offset="0.3" stop-color="#dfe9ff" stop-opacity="0.08"/><stop offset="1" stop-color="#dfe9ff" stop-opacity="0.02"/></linearGradient>`,
    `<linearGradient id="${id(
      'spec',
    )}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff" stop-opacity="0.32"/><stop offset="1" stop-color="#ffffff" stop-opacity="0"/></linearGradient>`,
    `<linearGradient id="${id('gold')}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${
      m.hi
    }"/><stop offset="0.45" stop-color="${m.mid}"/><stop offset="1" stop-color="${m.lo}"/></linearGradient>`,
    `<radialGradient id="${id(
      'face',
    )}" cx="50%" cy="38%" r="70%"><stop offset="0" stop-color="#1b1846"/><stop offset="1" stop-color="#070a1d"/></radialGradient>`,
    `<linearGradient id="${id('mark')}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${
      spent ? '#8f98bd' : '#8B00FE'
    }"/><stop offset="1" stop-color="${spent ? '#5d6690' : '#1F00FE'}"/></linearGradient>`,
    `<clipPath id="${id('clip')}"><rect x="28" y="96" width="144" height="116" rx="24"/></clipPath>`,
    '</defs>',
  )

  // light behind the lock and its shadow on the ground
  parts.push(`<ellipse cx="100" cy="150" rx="100" ry="76" fill="${url('halo')}"/>`)
  parts.push(`<ellipse cx="100" cy="213" rx="76" ry="7" fill="${url('ground')}"/>`)

  // the shackle: closed (its legs run into the body), or lifted out of it once the lock no longer protects anything (due, released)
  const sw = mini ? 22 : 20
  // open: the left leg stays in the body, the arc rises and the right leg stands clear of it
  const top = open ? 50 : 66
  const bar = `M62 118 V${top} A38 38 0 0 1 138 ${top} V${open ? 84 : 118}`
  parts.push(
    `<g class="shackle">`,
    `<path d="${bar}" stroke="#141a3e" stroke-width="${sw + 4}" fill="none"/>`,
    `<path d="${bar}" stroke="${url('bar')}" stroke-width="${sw}" fill="none"/>`,
    mini
      ? ''
      : `<path d="M57 112 V${top} A43 43 0 0 1 100 ${
          top - 43
        }" stroke="#ffffff" stroke-opacity="0.5" stroke-width="2.5" stroke-linecap="round" fill="none"/>`,
    '</g>',
  )

  // the body
  parts.push(
    `<rect x="28" y="96" width="144" height="116" rx="24" fill="${url('body')}" stroke="#05071a" stroke-width="2"/>`,
  )
  parts.push(`<rect x="28" y="96" width="144" height="116" rx="24" fill="${url('side')}"/>`)
  if (!mini) {
    // brushed metal: faint horizontal grain
    const grain: string[] = []
    for (let y = 104; y < 210; y += 5) grain.push(`M28 ${y} H172`)
    parts.push(
      `<path d="${grain.join(' ')}" stroke="#ffffff" stroke-opacity="0.035" stroke-width="1" clip-path="${url(
        'clip',
      )}"/>`,
    )
  }
  parts.push(
    `<rect x="33" y="101" width="134" height="106" rx="20" fill="none" stroke="${url('bevel')}" stroke-width="2"/>`,
  )
  parts.push(`<rect x="40" y="104" width="120" height="22" rx="11" fill="${url('spec')}"/>`)
  // where the shackle enters the body
  parts.push(
    `<ellipse cx="62" cy="99" rx="13" ry="3.6" fill="#03040f"/><ellipse cx="138" cy="99" rx="13" ry="3.6" fill="#03040f"/>`,
  )

  // the seam: a light in the status colour across the top plate (dim when released)
  parts.push(
    `<g class="seam">`,
    `<path d="M40 124 H160" stroke="${lit}" stroke-opacity="${
      spent ? 0.06 : 0.14
    }" stroke-width="12" stroke-linecap="round"/>`,
    `<path d="M40 124 H160" stroke="${lit}" stroke-opacity="${
      spent ? 0.12 : 0.32
    }" stroke-width="5" stroke-linecap="round"/>`,
    `<path d="M40 124 H160" stroke="${spent ? lit : '#d9f6fc'}" stroke-opacity="${
      spent ? 0.5 : 0.95
    }" stroke-width="1.6" stroke-linecap="round"/>`,
    '</g>',
  )

  if (!mini) {
    for (const [x, y] of [
      [44, 112],
      [156, 112],
      [44, 198],
      [156, 198],
    ]) {
      parts.push(
        `<circle cx="${x}" cy="${y}" r="3.4" fill="#141a3e"/><circle cx="${x}" cy="${y}" r="2.6" fill="${url(
          'bar',
        )}"/>`,
      )
    }
  }

  // the gold medal: a struck rim, the time served as a ring in the status colour, the official mark
  const teeth: string[] = []
  for (let i = 0; i < 40; i++) {
    const a = i * 9
    const [x1, y1] = polar(36, a - 4.5)
    const [x2, y2] = polar(40, a)
    const [x3, y3] = polar(36, a + 4.5)
    teeth.push(`M${n2(x1)},${n2(y1)} L${n2(x2)},${n2(y2)} L${n2(x3)},${n2(y3)}Z`)
  }
  parts.push(`<circle cx="${MX}" cy="${MY + 2}" r="40" fill="#03040f" fill-opacity="0.55"/>`)
  parts.push(`<path d="${teeth.join('')}" fill="${url('gold')}"/>`)
  parts.push(`<circle cx="${MX}" cy="${MY}" r="36" fill="${url('gold')}" stroke="${m.edge}" stroke-width="1"/>`)
  parts.push(
    `<circle cx="${MX}" cy="${MY}" r="27.5" fill="${url('face')}" stroke="${
      m.hi
    }" stroke-opacity="0.5" stroke-width="1"/>`,
  )
  const rr = 31.75
  const circ = 2 * Math.PI * rr
  const rw = mini ? 5 : 3.6
  parts.push(
    `<circle cx="${MX}" cy="${MY}" r="${rr}" fill="none" stroke="#070a1d" stroke-opacity="0.55" stroke-width="${rw}"/>`,
  )
  if (p > 0.002) {
    parts.push(
      `<circle class="arc" cx="${MX}" cy="${MY}" r="${rr}" fill="none" stroke="${lit}" stroke-width="${rw}" stroke-linecap="round" stroke-dasharray="${n2(
        circ * p,
      )} ${n2(circ)}" transform="rotate(-90 ${MX} ${MY})"/>`,
    )
  }
  parts.push(mark(uid, MX + 1.5, MY, mini ? 36 : 32))

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${PADLOCK_VIEWBOX}" fill="none">${parts.join('')}</svg>`
}

/** The padlock as a data URI, for an <img> (the share image). */
export const padlockDataUri = (o: PadlockOptions) =>
  `data:image/svg+xml;base64,${
    typeof Buffer !== 'undefined' ? Buffer.from(padlockSvg(o)).toString('base64') : btoa(padlockSvg(o))
  }`
